import { SettingApi } from "lib/api";
import { ApiHelper, Http } from "lib/api/base";

import { Section, SectionTemplate, SectionVersion } from "lib/data/model";
import { VersionState } from "lib/data/model/enumerations";
import { ValidationError } from "lib/data/transfer/ValidationError";
import { SerializableError } from "lib/data/types";


export class SectionApi {

	public static getOwnedSections = async (): Promise<Section[]> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/Section`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const section: Section[] = await ApiHelper.parseResponseBody(response);

				return section;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return [];
	}

	public static getSection = async (id?: number, cascade: boolean = false): Promise<Section | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Check ID
			if (id === undefined) {

				// Reset ID
				id = 1;
			}

			// Configure Url
			const url: string = `${baseUrl}/Section/${id}`;

			// Initialize Parameters
			const parameters: any = {};

			// Check Cascade
			if (cascade) {

				parameters.includePublished = true;
			}

			// Make Request
			const response: Response = await Http.httpGet(url, parameters);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const section: Section = await ApiHelper.parseResponseBody(response);

				return section;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static getHierarchy = async (id?: number): Promise<Section[]> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl && id) {

			// Configure Url
			const url: string = `${baseUrl}/Section/${id}/Hierarchy`;

			// Make Request
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const hierarchy: Section[] = await ApiHelper.parseResponseBody(response);

				return hierarchy;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return [];
	}

	public static saveSection = async (section: Section): Promise<Section | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			let url: string = `${baseUrl}/Section`;

			// Initialize Response
			let response: Response;

			// Check For Empty ID
			if (section.id) {

				// Update URL
				url += `/${section.id}`;

				// Make Request
				response = await Http.httpPut(url, section);
			}
			else {

				// Make Request
				response = await Http.httpPost(url, section);
			}

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const saved: Section = await ApiHelper.parseResponseBody(response);

				return saved;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static getTemplate = async (section: Section, cascade: boolean = false): Promise<SectionTemplate | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl && section.id !== undefined) {

			// Configure Url
			const url: string = `${baseUrl}/Section/${section.id}/Template`;

			// Initialize Parameters
			const parameters: any = {};

			// Check Record Cascading
			if (cascade) {

				// Set To Cascade
				parameters.all = true;
			}

			// Make Request
			const response: Response = await Http.httpGet(url, parameters);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const template: SectionTemplate = await ApiHelper.parseResponseBody(response);

				return template;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static getSectionVersions = async (sectionId: number, state?: VersionState): Promise<SectionVersion[]> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/Section/${sectionId}/Version`;

			// Initialize Response
			let response: Response;

			// Check For Requested State
			if (state !== undefined) {

				// Make Request
				response = await Http.httpGet(url, { state });
			}
			else {

				// Make Request
				response = await Http.httpGet(url, undefined);
			}

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const sectionVersions: SectionVersion[] = await ApiHelper.parseResponseBody(response);

				return sectionVersions;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return [];
	}

	public static getDraft = async (sectionId: number): Promise<SectionVersion | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/Section/${sectionId}/Draft`;

			// Initialize Response
			const response: Response = await Http.httpGet(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const sectionVersion: SectionVersion = await ApiHelper.parseResponseBody(response);

				return sectionVersion;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static publishDraft = async (sectionId: number, cascade?: boolean): Promise<boolean | ValidationError | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			let url: string = `${baseUrl}/Section/${sectionId}/Publish`;

			// Check For Cascade
			if (cascade !== undefined && cascade) {

				// Append URL
				url += ApiHelper.buildQueryString({ cascade });
			}

			// Initialize Response
			const response: Response = await Http.httpPost(url, undefined);

			// Check Response
			if (response.ok) {

				// Return Success
				return true;
			}
			else {

				// Switch On Status Code
				switch (response.status) {

					case 422: // Unprocessable Entity

						// Get Errors
						const errors: SerializableError | string = await ApiHelper.parseResponseBody(response);

						// Check For Empty String
						if (typeof errors === "string") {

							// Return Failure
							return false;
						}

						// Return Validation Errors
						return new ValidationError(errors);
				}
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static revertDraft = async (sectionId: number): Promise<SectionVersion | undefined> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/Section/${sectionId}/Draft`;

			// Initialize Response
			const response: Response = await Http.httpDelete(url, undefined);

			// Check Response
			if (response.ok) {

				// Parse Response Body
				const saved: SectionVersion = await ApiHelper.parseResponseBody(response);

				return saved;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return undefined;
	}

	public static deleteSection = async (sectionId: number): Promise<boolean> => {

		// Get Base Url
		const baseUrl = SettingApi.getApiBaseUrl();

		// Check Base Url
		if (baseUrl) {

			// Configure Url
			const url: string = `${baseUrl}/Section/${sectionId}`;

			// Initialize Response
			let response: Response;

			// Make Request
			response = await Http.httpDelete(url, undefined);

			// Check Response
			if (response.ok) {

				return true;
			}

			// TODO: Handle Errors
		}

		// Return Nothing
		return false;
	}
}
